/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import isNil from 'lodash.isnil';
import {
  getAccountId,
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '@ion/applications/src/helpers';
import {
  errorAddOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '@ion/applications/src/prompts/constants';
import {PageState} from '@ion/applications/src/types/pages';
import {gridDefaultPageSize} from '@ion/applications/src/utils/helpers';
import {error} from '@ion/core/src/logger/logger';
import {
  createGroup,
  createGroupMembers,
  deleteAdminGroup as destroyGroup,
  deleteAdminGroups as destroyGroups,
  deleteGroupMembers,
  getGroup,
  loadGroups,
  updateGroup,
  updateGroupMembers
} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  AuditChange,
  Entity,
  Group,
  GroupMembers,
  GroupsFilters
} from '@ion/core/src/types/entities';
import {
  HierLevel,
  LoadGroupsParams,
  ServiceError
} from '@ion/core/src/types/services';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {createModularSearchFilters} from '@ion/features/src/search/utils';
import {retryUpdate} from '@ion/services/src/helpers/retry';
import {AdminAction, AsyncState, PageInitializeAction} from '@/types/redux';
import {toggleUserPrompt} from '../../../store/actions/user-prompt';
import {getCurrentUser} from '../users/useAuthenticatedUser';

export const NAME                            = 'groups'; // Name of the reducer
export const INITIALIZE_GROUP                = 'INITIALIZE_GROUP'; // Initialize group ...
export const GROUPS_GRID_REFRESHED           = 'GROUPS_GRID_REFRESHED'; // Refresh grid ...
export const SET_GRID_PAGE_SIZE              = 'SET_GROUPS_GRID_PAGE_SIZE';
export const SET_GROUPS_GRID_COLUMNS         = 'SET_GROUPS_GRID_COLUMNS'; // Set grid columns
export const UPDATE_GROUPS_GRID_COLUMNS      = 'UPDATE_GROUPS_GRID_COLUMNS';
export const GET_GROUPS_REQUEST              = 'GET_GROUPS_REQUEST'; // Retrieve groups...
export const GET_GROUPS_RESPONSE             = 'GET_GROUPS_RESPONSE';
export const GET_GROUPS_ERROR                = 'GET_GROUPS_ERROR';

// grid sub groups
export const SET_VIEW_SUB_GROUPS             = 'SET_GROUP_VIEW_SUB_GROUPS';
export const SET_EXCLUDE_INHERITED           = 'SET_EXCLUDE_INHERITED';

// Add Groups ...
export const ADD_GROUP                       = 'ADD_GROUP';
export const ADD_GROUP_REQUEST               = 'ADD_GROUP_REQUEST';
export const ADD_GROUP_RESPONSE              = 'ADD_GROUP_RESPONSE';
export const ADD_GROUP_ERROR                 = 'ADD_GROUP_ERROR';

// Add Group members ...
export const ADD_GROUP_MEMBER_REQUEST        = 'ADD_GROUP_MEMBER_REQUEST';
export const ADD_GROUP_MEMBER_RESPONSE       = 'ADD_GROUP_MEMBER_RESPONSE';
export const ADD_GROUP_MEMBER_ERROR          = 'ADD_GROUP_ERROR';

// Edit Group members ...
export const EDIT_GROUP_MEMBER_REQUEST       = 'EDIT_GROUP_MEMBER_REQUEST';
export const EDIT_GROUP_MEMBER_RESPONSE      = 'EDIT_GROUP_MEMBER_RESPONSE';
export const EDIT_GROUP_MEMBER_ERROR         = 'EDIT_GROUP_ERROR';

// Delete Group members ...
export const DELETE_GROUP_MEMBER_REQUEST     = 'DELETE_GROUP_MEMBER_REQUEST';
export const DELETE_GROUP_MEMBER_RESPONSE    = 'DELETE_GROUP_MEMBER_RESPONSE';
export const DELETE_GROUP_MEMBER_ERROR       = 'DELETE_GROUP_ERROR';

// Group Edit ...
export const EDIT_GROUP                      = 'EDIT_GROUP';
export const EDIT_GROUP_REQUEST              = 'EDIT_GROUP_REQUEST';
export const EDIT_GROUP_RESPONSE             = 'EDIT_GROUP_RESPONSE';
export const EDIT_GROUP_ERROR                = 'EDIT_GROUP_ERROR';

// View Group ...
export const VIEW_GROUP                      = 'VIEW_GROUP';
export const VIEW_GROUP_REQUEST              = 'VIEW_GROUP_REQUEST';
export const VIEW_GROUP_RESPONSE             = 'VIEW_GROUP_RESPONSE';
export const VIEW_GROUP_ERROR                = 'VIEW_GROUP_ERROR';

// Delete Group ...
export const DELETE_GROUP                    = 'DELETE_GROUP';
export const DELETE_GROUP_REQUEST            = 'DELETE_GROUP_REQUEST';
export const DELETE_GROUP_RESPONSE           = 'DELETE_GROUP_RESPONSE';
export const DELETE_GROUP_ERROR              = 'DELETE_GROUP_ERROR';

//View Audit log ...
export const AUDIT_GROUP                     = 'AUDIT_GROUP';
export const AUDIT_GROUP_REQUEST             = 'AUDIT_GROUP_REQUEST';
export const AUDIT_GROUP_RESPONSE            = 'AUDIT_GROUP_RESPONSE';
export const AUDIT_GROUP_ERROR               = 'AUDIT_GROUP_ERROR';
export const AUDIT_GROUP_SHOW_CHANGES_DIALOG = 'AUDIT_GROUP_SHOW_CHANGES_DIALOG';

// bulk notification deletes ...
export const DELETE_ADMIN_GROUPS             = 'DELETE_ADMIN_GROUPS';
export const DELETE_ADMIN_GROUPS_REQUEST     = 'DELETE_ADMIN_GROUPS_REQUEST';
export const DELETE_ADMIN_GROUPS_RESPONSE    = 'DELETE_ADMIN_GROUPS_RESPONSE';
export const DELETE_ADMIN_GROUPS_ERROR       = 'DELETE_ADMIN_GROUPS_ERROR';

// search queries
export const UPDATE_GROUPS_SEARCH_QUERIES    = 'UPDATE_GROUPS_SEARCH_QUERIES';
export const RESET_GROUPS_SEARCH_QUERIES     = 'RESET_GROUPS_SEARCH_QUERIES';

/*
 * initialize Groups
 */
export const initializeGroups = (): PageInitializeAction => {
  return {type: INITIALIZE_GROUP};
};

/**
 * set Grid Columns
 */
export const setGridColumns = (columnState: ColumnState[]) => {
  return {type: SET_GROUPS_GRID_COLUMNS, columnState};
};

/**
 * Called when Grid Refreshed
 */
export const groupsGridRefreshed = (): PageInitializeAction => {
  return {type: GROUPS_GRID_REFRESHED};
};

/**
 * Invoked whenever user click on View Sub Accounts toggle button
 */
export const setGroupViewSubGroups = (viewSubGroups: boolean = false) => ({
  type : SET_VIEW_SUB_GROUPS, viewSubGroups
});

/**
 * Invoked whenever user click on Exclude Inherited toggle button
 */
export const setGroupExcludeInheritedGroups = (includeInheritedGroups: boolean = false) => ({
  type : SET_EXCLUDE_INHERITED, includeInheritedGroups
});

// Updates the modular search queries for Admin AEMPConfig
export const updateGroupsSearchQueries = (searchParams: GroupsFilters) => ({
  type : UPDATE_GROUPS_SEARCH_QUERIES, searchParams
});

// Resets the Peg Labels filters state
export const resetGroupsSearchQueries = () => ({
  type : RESET_GROUPS_SEARCH_QUERIES
});

/**
 * Retrieve slected Group details
 */
export const getGroups = ({
  pageId = 1,
  pageSize = 10,
  sort,
  filter,
  includeSubGroups = false,
  includeGlobalSearch = false,
  excludeInheritedGroups = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_GROUPS_REQUEST});
    const {
      groups: {
        search
      }
    } = getState();
    const user = getCurrentUser(getState);
    const data: LoadGroupsParams = {
      accountId : getAccountId(getState),
      pageId,
      pageSize,
      includeSubGroups,
      includeGlobalSearch,
      excludeInheritedGroups
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    data.filterOn = {
      ...data.filterOn,
      ...(!includeGlobalSearch && createModularSearchFilters(search))
    };
    try {
      const {groups: items, totalCount} = await loadGroups(data);
      dispatch({type: GET_GROUPS_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      callbackFn({error: err, items: [], totalCount: 0});
      dispatch({type: GET_GROUPS_ERROR, error: err});
      return err;
    }
  };
};

/**
 * Invoked when the Add Admin Group editor is to be displayed or removed.
 */
export const addGroup = (show = true) => ({
  type : ADD_GROUP, show
});

/**
 * Invoked when a new Group is to be created, delegating to the createGroup
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const addGroupConfirm = (group: Group) => {
  const groupMember = getGroupMember(group);
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: ADD_GROUP_REQUEST});
    try {
      const created = await createGroup(group);
      if (groupMember) {
        dispatch(addMembersToGroup(created, groupMember));
      } else {
        dispatch({type: ADD_GROUP_RESPONSE, group: created});
        dispatch(toggleUserPrompt(successAddOptions(Entity.group)));
      }
    } catch (err) {
      dispatch({type: ADD_GROUP_ERROR, error: err});
      dispatch(toggleUserPrompt(errorAddOptions(Entity.group, err)));
      error(err);
    }
  };
};

/**
 * Invoked when a new Group is to be created, delegating to the createGroup
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const addMembersToGroup = (group: Group, groupMembers: GroupMembers) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: ADD_GROUP_MEMBER_REQUEST});
    try {
      const created = await createGroupMembers(group.id, groupMembers);
      dispatch({type: ADD_GROUP_MEMBER_RESPONSE, group: created});
      dispatch(toggleUserPrompt(successAddOptions(Entity.group)));
    } catch (err) {
      groupUpdateError(err, dispatch);
    }
  };
};

/**
 * Invoked when the Edit Admin Group editor is to be displayed or removed.
 */
export const editGroup = (group: Group, show = true) => ({
  type : EDIT_GROUP, group, show
});

/**
 * Invoked when an existing Group gets updated, delegating to the updateGroup
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const editGroupConfirm = (group: Group, deleteMembers: Group) => {
  const groupMember = getGroupMember(group);
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_GROUP_REQUEST});
    try {
      const updated = await retryUpdate({
        newEntity    : group,
        updateEntity : updateGroup,
        getEntity    : getSingleGroup,
        onError      : groupUpdateError,
        dispatch
      });
      if (updated) {
        if (!groupMember) {
          if (deleteMembers?.devices.length || deleteMembers?.assets.length) {
            await dispatch(updateMembers(updated, null, deleteMembers));
          } else {
            dispatch({type: EDIT_GROUP_RESPONSE, group: updated});
            dispatch(toggleUserPrompt(successEditOptions(Entity.group)));
          }
        } else {
          dispatch(updateMembers(updated, groupMember, deleteMembers));
        }
      }
    } catch (err) {
      dispatch({type: EDIT_GROUP_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(Entity.group)));
    }
  };
};

/**
 * Invoked when an existing Group gets resource out of state, delegating to the fetch
 * latest group details successful response or error action accordingly.
 */
const getSingleGroup = async(groupID: number) => {
  try {
    const group = await getGroup(groupID);
    return {updated: group, error: ''};
  } catch (err) {
    error(err);
    return {updated: null, error: err};
  }
};

/**
 * Invoked when an existing Group gets updated or fetched, CTC API
 * and dispatching a successful or error action accordingly.
 */
const groupUpdateError = (err, dispatch: Function) => {
  dispatch({type: EDIT_GROUP_ERROR, error: err});
  dispatch(toggleUserPrompt(errorEditOptions(Entity.groupmember, err)));
  error(err);
};

/**
 * Invoked when a Group gets updated, delegating to the UpdateGroup
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const updateMembers = (group: Group, groupMembers: GroupMembers, deleteMembers: Group = {}) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_GROUP_MEMBER_REQUEST});
    try {
      let updated = [];
      if (groupMembers) {
        updated = await updateGroupMembers(group.id, groupMembers);
      }
      if (deleteMembers?.devices?.length || deleteMembers?.assets?.length) {
        const {devices, assets} = deleteMembers;
        const members = {
          account : entityId(group.account),
          devices : {
            value : devices?.join(' OR ') || ''
          },
          assets : {
            value : assets?.join(', ') || ''
          }
        };
        const groupMember = getGroupMember(members, deleteMembers);
        await dispatch(removeGroupMembers(group, groupMember));
      }
      dispatch({type: EDIT_GROUP_MEMBER_RESPONSE, group: updated});
      dispatch(toggleUserPrompt(successEditOptions(Entity.group)));
    } catch (err) {
      dispatch({type: EDIT_GROUP_MEMBER_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(Entity.groupmember)));
      error(err);
    }
  };
};

/**
 * Invoked when members needs to be removed from the Group.Updates the group members and
 * dispatches a successful response or error action accordingly.
 */
export const removeGroupMembers = (group: Group, groupMemberObj: GroupMembers) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_GROUP_MEMBER_REQUEST});
    try {
      const deleted = await deleteGroupMembers(group.id, groupMemberObj);
      dispatch({type: DELETE_GROUP_MEMBER_RESPONSE, group: deleted});
    } catch (err) {
      dispatch({type: DELETE_GROUP_MEMBER_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked when the View Group dialog is to be displayed or removed.
 */
export const viewGroup = (group: Group, show = true) => ({
  type : VIEW_GROUP, group, show
});

/**
 * Invoked when an selected Group is to be displayed, delegating to the getGroup
 * CTC API and dispatching a response of selected aempconfig.
 */
export const viewAdminGroup = (group: Group) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: VIEW_GROUP_REQUEST});
    try {
      const result = await getGroup(group.id);
      dispatch({type: VIEW_GROUP_RESPONSE, group: result});
    } catch (err) {
      dispatch({type: VIEW_GROUP_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked when the Group Audit is to be displayed or removed.
 */
export const groupAudit = (group: Group, show = true) => ({
  type : AUDIT_GROUP, group, show
});

/**
 * Invoked when the View link clicked on the Group Audit Row
 * to show the entire row audit record in separate dialog.
 */
export const showAuditChangesDialog = (auditRecord: AuditChange, show = true) => ({
  type : AUDIT_GROUP_SHOW_CHANGES_DIALOG, auditRecord, show
});

/**
 * Invoked when the Delete Admin Group Dialog is to be displayed or removed.
 */
export const deleteAdminGroup = (group: Group, show = true) => ({
  type : DELETE_GROUP, group, show
});

/**
 * Invoked when an existing Group is to be deleted, delegating to the destroy Group
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const deleteGroup = (group: Group) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_GROUP_REQUEST});
    try {
      const deleted = await destroyGroup(group);
      dispatch({type: DELETE_GROUP_RESPONSE, group: deleted});
    } catch (err) {
      dispatch({type: DELETE_GROUP_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked when the Bulk Delete Admin groups Dialog is to be displayed or removed.
 */
export const deleteAdminGroups = (groups: Group[], show = true) => ({
  type : DELETE_ADMIN_GROUPS, groups, show
});

/**
 * Invoked when a collection of existing groups are to be deleted, delegating to
 * the destroyGroups CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const deleteGroups = (groups: Group[]) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_GROUPS_REQUEST});
    try {
      const {results, errors} = await destroyGroups(groups);
      if (errors.length > 0) {
        dispatch({type: DELETE_ADMIN_GROUPS_ERROR, error: errors});
        return;
      }
      dispatch({type: DELETE_ADMIN_GROUPS_RESPONSE, groups: results});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_GROUPS_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Check the Group object has group member.
 */
export const getGroupMember = (group, deleteMembers = {}) => {
  let groupMember;
  if (group.devices?.value || group.assets?.value) {
    const normalizeObj = normalizeGroup(group, deleteMembers);
    group = normalizeObj.group;
    groupMember = normalizeObj.groupMember;
  }
  return groupMember;
};

/**
 * Invoked when a new GroupMember is to be created, delegating to the createGroupMembers
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const setGroupMember = (GroupMember, deleteMembers) => {
  const members         = {searchCriteria: []};
  const assetHierLevel  = deleteMembers?.assets?.length ? HierLevel.ALL : '';
  const deviceHierLevel = deleteMembers?.devices?.length ? HierLevel.ALL : '';
  const assetSearch     = {memberSearch: {type: '', searchTerms: {}, hierLevel: assetHierLevel}};
  const deviceSearch    = {smartSearch: {type: '', queryString: '', hierLevel: deviceHierLevel, accountId: null}};
  if (GroupMember.assets?.value) {
    assetSearch.memberSearch.type        = 'asset';
    assetSearch.memberSearch.searchTerms = {
      account : entityId(GroupMember?.account),
      id      : GroupMember.assets.value
    };
    if (!isNil(GroupMember?.assetSwitch)) {
      assetSearch.memberSearch.hierLevel = GroupMember?.assetSwitch?.hierLevel;
    }
    members.searchCriteria.push(assetSearch);
  }

  if (GroupMember.devices?.value) {
    deviceSearch.smartSearch.type        = 'device';
    deviceSearch.smartSearch.queryString = `groupIds:${deleteMembers.groupId}`;
    if (!isNil(GroupMember?.deviceSwitch)) {
      deviceSearch.smartSearch.hierLevel = GroupMember?.deviceSwitch?.hierLevel;
    }
    if (!deleteMembers?.allDevicesClear) {
      deviceSearch.smartSearch.queryString = `id:(${GroupMember.devices.value})`;
      deviceSearch.smartSearch.accountId   = entityId(GroupMember?.account);
    }
    members.searchCriteria.push(deviceSearch);
  }
  return members;
};

/**
 * Invoked when the Group has members and normalize the group.
 */
export const normalizeGroup = (group, deleteMembers) => {
  const {devices, assets} = group;
  const groupMember = {
    devices      : devices,
    assets       : assets,
    account      : group.account,
    deviceSwitch : group.deviceSwitch,
    assetSwitch  : group.assetSwitch
  };
  delete group.devices;
  delete group.assets;
  return {
    group       : group,
    groupMember : setGroupMember(groupMember, deleteMembers)
  };
};

/**
 * Invoked whenever user change the Grid row per page dropdown
 */
export const setGroupsGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});

/**
 * REDUCERS
 */
export interface GroupsAction extends AdminAction {
  group?: Group;
  groups?: Group[];
  searchParams?: GroupsFilters;
};

interface GroupsActionState extends AsyncState {
  selected: Group | Group[] | null;
  result: Group | Group[] | null;
  record?: Group | Group[] | null;
  isEditing: boolean;
  isView: boolean;
};
export interface GroupsAuditState extends GroupsActionState {
  showChangesDialog?: boolean;
  auditRecord?: AuditRecordType;
}
export interface GroupsState extends PageState {
  groups: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    includeSubGroups: boolean,
    includeInheritedGroups: boolean,
    hasUpdate: boolean,
    pageSize: number,
  },
  add: GroupsActionState,
  edit: GroupsActionState,
  view: GroupsActionState,
  destroy: GroupsActionState,
  audit: GroupsAuditState,
  bulkDelete: GroupsActionState,
  search: GroupsFilters
};

// defines the user preference key for the admin groups page ...
export const PREFERENCE = 'groups';

// defines the initial state for the reducer ...
export const initialState: GroupsState = {
  isInitialized : false,
  groups        : {
    error   : null,
    loading : false
  },
  grid : {
    columnState            : null,
    preferedColumnState    : null,
    hasUpdate              : false,
    includeSubGroups       : false,
    includeInheritedGroups : false,
    pageSize               : gridDefaultPageSize
  },
  add : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  edit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  view : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  audit : {
    isEditing         : false,
    selected          : null,
    loading           : false,
    error             : null,
    result            : null,
    isView            : false,
    showChangesDialog : false,
    auditRecord       : null
  },
  destroy : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  bulkDelete : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    record    : null,
    isView    : false
  },
  search : {}
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_GROUP] : draft => {
    draft.isInitialized = true;
    const preference = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState = preference;
    draft.grid.preferedColumnState = preference;
    draft.bulkDelete.error = draft.bulkDelete.record = null;
  },
  [SET_GROUPS_GRID_COLUMNS] : ({grid, groups}: GroupsState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : groups.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GROUPS_GRID_COLUMNS] : ({grid}: GroupsState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [GROUPS_GRID_REFRESHED] : (
    {add, edit, view, destroy}: GroupsState
  ) => {
    add.result = null;
    edit.result = null;
    view.result = null;
    destroy.result = null;
  },
  [GET_GROUPS_REQUEST] : ({groups}: GroupsState) => {
    groups.loading = true;
  },
  [GET_GROUPS_RESPONSE] : ({groups}: GroupsState) => {
    groups.loading = false;
  },
  [GET_GROUPS_ERROR] : (
    {groups}: GroupsState, {error}: GroupsAction
  ) => {
    groups.loading = false;
    groups.error   = error;
  },
  [ADD_GROUP] : ({add}: GroupsState, {show}: GroupsAction) => {
    add.isEditing = show;
    add.result    = null;
  },
  [ADD_GROUP_REQUEST] : ({add}: GroupsState) => {
    add.loading = true;
    add.error   = add.result = null;
  },
  [ADD_GROUP_RESPONSE] : ({add}: GroupsState,
    {group}: GroupsAction) => {
    add.loading   = false;
    add.isEditing = false;
    add.result    = group;
  },
  [ADD_GROUP_ERROR] : ({add}: GroupsState, {error}: GroupsAction) => {
    add.loading   = false;
    add.error     = error;
  },
  [EDIT_GROUP] : ({edit}: GroupsState, {group, show}: GroupsAction) => {
    edit.selected  = show ? group : null;
    edit.isEditing = show;
    edit.result    = null;
  },
  [EDIT_GROUP_REQUEST] : ({edit}: GroupsState) => {
    edit.loading = true;
    edit.error   = edit.result = null;
  },
  [EDIT_GROUP_RESPONSE] : ({edit}: GroupsState,
    {group}: GroupsAction) => {
    edit.loading   = false;
    edit.isEditing = false;
    edit.result    = group;
  },
  [EDIT_GROUP_ERROR] : ({edit}: GroupsState, {error}: GroupsAction) => {
    edit.loading   = false;
    edit.error     = error;
  },
  [VIEW_GROUP] : ({view}: GroupsState, {group, show}: GroupsAction) => {
    view.selected  = show ? group : null;
    view.isEditing = show;
    view.result    = null;
  },
  [VIEW_GROUP_REQUEST] : ({view}: GroupsState) => {
    view.loading = true;
    view.error   = view.result = null;
  },
  [VIEW_GROUP_RESPONSE] : ({view}: GroupsState, {result}: GroupsAction) => {
    view.loading   = false;
    view.result    = result;
  },
  [VIEW_GROUP_ERROR] : ({view}: GroupsState, {error}: GroupsAction) => {
    view.loading   = false;
    view.error     = error;
  },
  [AUDIT_GROUP] : ({audit}: GroupsState, {group, show}: GroupsAction) => {
    audit.selected = show ? group : null;
    audit.isEditing = show;
    audit.result = null;
  },
  [AUDIT_GROUP_REQUEST] : ({audit}: GroupsState) => {
    audit.loading = true;
    audit.error = audit.result = null;
  },
  [AUDIT_GROUP_RESPONSE] : ({audit}: GroupsState, {result}: GroupsAction) => {
    audit.loading = false;
    audit.isEditing = false;
    audit.result = result;
  },
  [AUDIT_GROUP_ERROR] : ({audit}: GroupsState, {error}: GroupsAction) => {
    audit.loading = false;
    audit.error = error;
  },
  [AUDIT_GROUP_SHOW_CHANGES_DIALOG] : ({audit}: GroupsState, {auditRecord, show}) => {
    audit.showChangesDialog = show;
    audit.auditRecord = auditRecord;
  },
  [DELETE_GROUP] : ({destroy}: GroupsState, {group, show}: GroupsAction) => {
    destroy.selected  = show ? group : null;
    destroy.isEditing = show;
    destroy.result    = null;
  },
  [DELETE_GROUP_REQUEST] : ({destroy}: GroupsState) => {
    destroy.loading = true;
    destroy.error   = destroy.result = null;
  },
  [DELETE_GROUP_RESPONSE] : ({destroy}: GroupsState, {group}: GroupsAction) => {
    destroy.loading = destroy.isEditing = false;
    destroy.result = group;
    destroy.selected = null;
  },
  [DELETE_GROUP_ERROR] : ({destroy}: GroupsState, {error}: GroupsAction) => {
    destroy.loading   = false;
    destroy.error     = error;
  },
  [DELETE_ADMIN_GROUPS] : (
    {bulkDelete}: GroupsState, {groups, show}: GroupsAction
  ) => {
    bulkDelete.selected = show ? groups : null;
    bulkDelete.isEditing = show;
    bulkDelete.result = null;
    bulkDelete.record = null;
  },
  [DELETE_ADMIN_GROUPS_REQUEST] : ({bulkDelete}: GroupsState) => {
    bulkDelete.loading = true;
    bulkDelete.error = bulkDelete.result = bulkDelete.record = null;
  },
  [DELETE_ADMIN_GROUPS_RESPONSE] : (
    {bulkDelete}: GroupsState, {groups}: GroupsAction
  ) => {
    bulkDelete.loading = bulkDelete.isEditing = false;
    bulkDelete.result = bulkDelete.record = groups;
    bulkDelete.selected = null;
  },
  [DELETE_ADMIN_GROUPS_ERROR] : (
    {bulkDelete}: GroupsState, {error}: GroupsAction
  ) => {
    bulkDelete.loading = false;
    bulkDelete.error = error;
  },
  [ADD_GROUP_MEMBER_REQUEST] : ({add}: GroupsState) => {
    add.loading = true;
    add.error   = add.result = null;
  },
  [ADD_GROUP_MEMBER_RESPONSE] : ({add}: GroupsState, {group}: GroupsAction) => {
    add.loading   = false;
    add.isEditing = false;
    add.result    = group;
  },
  [ADD_GROUP_MEMBER_ERROR] : ({add}: GroupsState, {error}: GroupsAction) => {
    add.loading   = false;
    add.error     = error;
  },
  [EDIT_GROUP_MEMBER_REQUEST] : ({edit}: GroupsState) => {
    edit.loading = true;
    edit.error   = edit.result = null;
  },
  [EDIT_GROUP_MEMBER_RESPONSE] : ({edit}: GroupsState,
    {group}: GroupsAction) => {
    edit.loading   = false;
    edit.isEditing = false;
    edit.result    = group;
  },
  [EDIT_GROUP_MEMBER_ERROR] : ({edit}: GroupsState, {error}: GroupsAction) => {
    edit.loading   = false;
    edit.error     = error;
  },
  [UPDATE_GROUPS_SEARCH_QUERIES] : ({search}: GroupsState, {searchParams}) => {
    search.name = searchParams.name || null;
  },
  [RESET_GROUPS_SEARCH_QUERIES] : ({search}: GroupsState) => {
    search.name = null;
  },
  [SET_VIEW_SUB_GROUPS] : ({grid}: GroupsState, {viewSubGroups}) => {
    grid.includeSubGroups = viewSubGroups;
  },
  [SET_EXCLUDE_INHERITED] : ({grid}: GroupsState, {includeInheritedGroups}) => {
    grid.includeInheritedGroups = includeInheritedGroups;
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: GroupsState, {pageSize}) => {
    grid.pageSize = pageSize;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: GroupsState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
